import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Bot,
  ShieldCheck,
  AlertTriangle,
  XCircle,
  Eye,
  Building2,
  MessageSquare,
  Users,
  ExternalLink
} from 'lucide-react';

export default function UsoAI() {
  return (
    <div className="space-y-6 max-w-4xl mx-auto pb-12" data-testid="uso-ai-page">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#0055BB] to-[#003d88] text-white flex items-center justify-center">
            <Bot className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Uso dell&apos;Intelligenza Artificiale
            </h1>
            <p className="text-sm text-gray-500">
              Trasparenza sui sistemi AI utilizzati in AssieticaGest
            </p>
          </div>
        </div>
        <p className="text-sm text-gray-400 mt-2">
          Conforme al Regolamento UE 2024/1689 (AI Act) e GDPR • Ultima revisione: Febbraio 2026
        </p>
      </div>

      {/* Il nostro impegno */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            Il nostro impegno
          </CardTitle>
        </CardHeader>
        <CardContent className="prose prose-sm max-w-none text-slate-700 space-y-3">
          <p>
            AssieticaGest è progettata per essere <strong>trasparente e conforme</strong> al
            Regolamento UE 2024/1689 (AI Act) sull&apos;Intelligenza Artificiale, entrato in
            applicazione progressiva dal 2 agosto 2024.
          </p>
          <p>
            Crediamo che tu debba sapere <strong>quando</strong> e <strong>come</strong> stai
            interagendo con sistemi automatizzati, quali dati vengono trattati e quali sono
            i tuoi diritti.
          </p>
        </CardContent>
      </Card>

      {/* Dove usiamo l'AI */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-primary" />
            Dove usiamo l&apos;Intelligenza Artificiale
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-700">
            Attualmente in AssieticaGest è integrata <strong>una sola funzionalità basata su AI</strong>:
          </p>

          <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center shrink-0">
                <Bot className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1 space-y-2">
                <h3 className="font-semibold text-slate-900">🤖 Chatbot Assistente Virtuale</h3>
                <p className="text-sm text-slate-600">
                  Un assistente virtuale che risponde alle tue domande sull&apos;uso del gestionale
                  basandosi sul manuale d&apos;uso ufficiale.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-slate-700 pt-2">
                  <div><strong>Livello di rischio AI Act:</strong> Minimo</div>
                  <div><strong>Modello:</strong> OpenAI GPT-4o-mini</div>
                  <div><strong>Riferimento normativo:</strong> Art. 50 AI Act</div>
                  <div><strong>Fornitore integrazione:</strong> Emergent</div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cosa NON facciamo */}
      <Card className="border-green-200 bg-green-50/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-green-800">
            <XCircle className="h-5 w-5" />
            Cosa NON facciamo con l&apos;AI
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-slate-700">
          <p>Per essere chiari, AssieticaGest <strong>NON</strong>:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Effettua profilazione automatica dei soci o volontari</li>
            <li>Prende decisioni automatizzate sull&apos;ammissione o esclusione dei soci</li>
            <li>Valuta il merito, le performance o il comportamento delle persone iscritte</li>
            <li>Usa dati biometrici, riconoscimento facciale o vocale</li>
            <li>Genera automaticamente contenuti pubblicati con la tua identità</li>
            <li>Effettua analisi predittive su comportamenti umani</li>
            <li>Utilizza tecniche subliminali o manipolative (vietate dall&apos;Art. 5 AI Act)</li>
            <li>Vende i tuoi dati o quelli dei tuoi soci a terzi</li>
          </ul>
        </CardContent>
      </Card>

      {/* Trasparenza contenuti */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5 text-primary" />
            Trasparenza sui contenuti generati
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-slate-700">
          <p>Ogni volta che interagisci con il chatbot AI:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Sei chiaramente informato che stai parlando con un&apos;AI (Art. 50.1 AI Act)</li>
            <li>Il testo generato è etichettato come contenuto AI (Art. 50.2 AI Act)</li>
            <li>Vengono mostrati i limiti e le avvertenze del sistema</li>
            <li>Puoi verificare le fonti nel manuale d&apos;uso</li>
            <li>Ti viene richiesto un consenso esplicito al primo utilizzo</li>
          </ul>
        </CardContent>
      </Card>

      {/* Responsabilità e limiti */}
      <Card className="border-amber-200 bg-amber-50/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-amber-900">
            <AlertTriangle className="h-5 w-5" />
            Responsabilità e limiti
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-slate-700">
          <ul className="list-disc pl-5 space-y-1">
            <li>
              L&apos;AI può <strong>commettere errori</strong>: verifica sempre le informazioni critiche
            </li>
            <li>
              Per pareri normativi, fiscali o legali consulta un <strong>professionista qualificato</strong>{' '}
              (commercialista, avvocato)
            </li>
            <li>
              Non condividere con il chatbot <strong>dati sensibili di terzi</strong>{' '}
              (soci, volontari, assistiti, informazioni sanitarie, ecc.)
            </li>
            <li>
              Le risposte del chatbot hanno valore <strong>orientativo, non ufficiale</strong>
            </li>
          </ul>
        </CardContent>
      </Card>

      {/* Governance interna */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-primary" />
            Governance interna
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-slate-700">
          <p>Il team di AssieticaGest:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Monitora periodicamente le prestazioni del chatbot</li>
            <li>Aggiorna il manuale d&apos;uso (knowledge base) per migliorare le risposte</li>
            <li>Rimuove immediatamente il servizio in caso di malfunzionamenti gravi</li>
            <li>Non usa l&apos;AI per finalità di marketing profilato</li>
            <li>Ha adottato una Policy Interna per l&apos;uso responsabile dell&apos;AI</li>
          </ul>
        </CardContent>
      </Card>

      {/* Segnala un problema */}
      <Card className="border-blue-200 bg-blue-50/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-blue-900">
            <Users className="h-5 w-5" />
            Segnala un problema
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-slate-700">
          <p>
            Se noti risposte errate, discriminatorie o problematiche del chatbot,
            scrivi immediatamente a:
          </p>
          <p className="text-base font-semibold">
            <a href="mailto:privacy@assietica.it" className="text-primary hover:underline">
              privacy@assietica.it
            </a>
          </p>
          <p className="text-xs text-slate-500">Rispondiamo entro 5 giorni lavorativi.</p>
        </CardContent>
      </Card>

      {/* Riferimenti normativi */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ExternalLink className="h-5 w-5 text-primary" />
            Riferimenti normativi
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-1 text-sm text-slate-700">
          <ul className="list-disc pl-5 space-y-1">
            <li>
              <a
                href="https://eur-lex.europa.eu/eli/reg/2024/1689/oj"
                target="_blank"
                rel="noreferrer"
                className="text-primary hover:underline"
              >
                Regolamento UE 2024/1689 (AI Act)
              </a>
            </li>
            <li>
              <a
                href="https://gdpr-info.eu/"
                target="_blank"
                rel="noreferrer"
                className="text-primary hover:underline"
              >
                Regolamento UE 2016/679 (GDPR)
              </a>
            </li>
            <li>
              <a
                href="https://www.garanteprivacy.it/"
                target="_blank"
                rel="noreferrer"
                className="text-primary hover:underline"
              >
                Garante per la Protezione dei Dati Personali
              </a>
            </li>
            <li>
              <a
                href="https://openai.com/enterprise-privacy"
                target="_blank"
                rel="noreferrer"
                className="text-primary hover:underline"
              >
                OpenAI Enterprise Privacy
              </a>
            </li>
          </ul>
          <p className="pt-3 text-xs text-slate-400">
            Ultima revisione: Febbraio 2026. Questa pagina viene aggiornata al variare dei
            sistemi AI integrati.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
